import React, { useRef, useEffect, useState } from "react";
import { recordWebcamCheck } from "../../services/antiFraudService";
import { withFixedWebmDuration } from "../../utils/recordingUtils";

const MORPHCAST_LICENSE_KEY = process.env.REACT_APP_MORPHCAST_LICENSE_KEY || "1ab1624d4c6c9a826a4e04e4b6188937392b7f52debd";
const WEBSOCKET_URL = process.env.REACT_APP_WEBSOCKET_URL || "ws://34.46.237.197:8080";
const WEBCAM_CHECK_INTERVAL = 15 * 60 * 1000; // 15 minutes in milliseconds

/**
 * Webcam Recorder Component
 * Records webcam video with audio and collects emotion data using MorphCast SDK
 * Includes anti-fraud face validation checks every 15 minutes
 */
const WebcamRecorder = React.forwardRef(({ onRecordingStart, onRecordingStop, onEmotionData, recordingTimestamp, username, recordingId }, ref) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const morphCastRef = useRef(null);
  const websocketRef = useRef(null);
  const frameIntervalRef = useRef(null);
  const webcamCheckIntervalRef = useRef(null);
  const emotionDataRef = useRef([]);
  const latestEmotionDataRef = useRef({ arousal: 0, valence: 0, attention: null });
  const webcamChecksCountRef = useRef(0);
  const recordingIdRef = useRef(recordingId);
  const startedAtRef = useRef(null);
  recordingIdRef.current = recordingId;
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState(null);
  const [faceDetected, setFaceDetected] = useState(false);

  // Initialize MorphCast SDK
  useEffect(() => {
    if (typeof window.CY === "undefined") {
      console.error("MorphCast SDK not loaded");
      setError("MorphCast SDK failed to load");
      return;
    }

    const initializeMorphCast = async () => {
      try {
        const cy = await window.CY.loader()
          .licenseKey(MORPHCAST_LICENSE_KEY)
          .addModule(window.CY.modules().FACE_DETECTOR.name)
          .addModule(window.CY.modules().FACE_EMOTION.name, { smoothness: 0.7 })
          .addModule(window.CY.modules().FACE_AROUSAL_VALENCE.name, { smoothness: 0.7 })
          .load();

        morphCastRef.current = cy;

        // Listen for arousal/valence events
        window.addEventListener(window.CY.modules().FACE_AROUSAL_VALENCE.eventName, handleEmotionEvent);
      } catch (err) {
        console.error("Error initializing MorphCast SDK:", err);
        setError("Failed to initialize emotion detection");
      }
    };

    initializeMorphCast();

    return () => {
      if (morphCastRef.current) {
        morphCastRef.current.stop();
      }
      window.removeEventListener(window.CY.modules().FACE_AROUSAL_VALENCE.eventName, handleEmotionEvent);
    };
  }, []);

  // Handle emotion events from MorphCast SDK
  const handleEmotionEvent = (event) => {
    if (!event.detail || !event.detail.output) return;

    const arousal = event.detail.output.arousal;
    const valence = event.detail.output.valence;
    const timestamp = new Date().toISOString();

    // Check if MorphCast provides attention metric
    const attention = event.detail.output.attention || event.detail.output.Attention || null;

    const emotionData = {
      arousal,
      valence,
      attention,
      timestamp,
    };

    // Update latest emotion data for face validation checks
    latestEmotionDataRef.current = {
      arousal,
      valence,
      attention,
    };

    // Determine if face is detected (if arousal and valence are both 0 or null/undefined, no face)
    const isFaceDetected = (arousal !== 0 || valence !== 0) && (arousal !== null && valence !== null);
    setFaceDetected(isFaceDetected);

    // Store for file upload (omit attention if not available)
    const dataToStore = attention !== null 
      ? JSON.stringify(emotionData)
      : JSON.stringify({ arousal, valence, timestamp });
    emotionDataRef.current.push(dataToStore);

    // Send via WebSocket if connected
    if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
      try {
        websocketRef.current.send(JSON.stringify(emotionData));
      } catch (err) {
        console.error("Error sending emotion data via WebSocket:", err);
      }
    }

    // Notify parent component
    if (onEmotionData) {
      onEmotionData(emotionData);
    }
  };

  // Perform periodic webcam check for anti-fraud validation
  const performWebcamCheck = async () => {
    const id = recordingIdRef.current;
    if (!id) {
      console.warn("Cannot perform webcam check: recordingId not available");
      return;
    }

    try {
      const { arousal, valence, attention } = latestEmotionDataRef.current;
      
      // Face is detected if arousal and valence are not both 0 and not null
      const isFaceDetected = (arousal !== 0 || valence !== 0) && (arousal !== null && valence !== null);
      
      // Calculate confidence based on the magnitude of arousal/valence
      // Higher values indicate stronger detection
      const magnitude = Math.sqrt(arousal * arousal + valence * valence);
      const confidence = Math.min(magnitude / 2, 1); // Normalize to 0-1 range
      
      const checkData = {
        faceDetected: isFaceDetected,
        arousal,
        valence,
        attention,
        confidence: confidence || 0.5,
        timestamp: new Date().toISOString(),
      };

      // Record check on backend
      await recordWebcamCheck(id, checkData);
      webcamChecksCountRef.current += 1;

      console.log(`Webcam check #${webcamChecksCountRef.current}:`, {
        faceDetected: isFaceDetected,
        arousal,
        valence,
        attention,
      });

      // Show warning if face not detected
      if (!isFaceDetected) {
        console.warn("⚠️ Face not detected in webcam check. Please ensure your face is visible.");
        // Could trigger a UI warning here
      }
    } catch (error) {
      console.error("Error performing webcam check:", error);
      // Continue recording even if check fails - don't interrupt user
    }
  };

  // Start periodic webcam monitoring for anti-fraud
  const startWebcamMonitoring = () => {
    // Perform first check immediately
    setTimeout(() => {
      performWebcamCheck();
    }, 5000); // Wait 5 seconds after start for Morphcast to initialize

    // Then check every 15 minutes
    webcamCheckIntervalRef.current = setInterval(() => {
      performWebcamCheck();
    }, WEBCAM_CHECK_INTERVAL);
  };

  // Stop webcam monitoring
  const stopWebcamMonitoring = () => {
    if (webcamCheckIntervalRef.current) {
      clearInterval(webcamCheckIntervalRef.current);
      webcamCheckIntervalRef.current = null;
    }
  };

  // recordingId arrives from the parent after webcam.start() has often already run.
  useEffect(() => {
    if (!isRecording || !recordingId || webcamCheckIntervalRef.current) return undefined;
    startWebcamMonitoring();
    return () => {
      stopWebcamMonitoring();
    };
  }, [isRecording, recordingId]);

  // Connect to WebSocket for real-time emotion streaming
  const connectWebSocket = () => {
    try {
      const ws = new WebSocket(WEBSOCKET_URL);
      ws.onopen = () => {
        console.log("WebSocket connected for emotion streaming");
      };
      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
      };
      ws.onclose = () => {
        console.log("WebSocket closed");
      };
      websocketRef.current = ws;
    } catch (err) {
      console.error("Failed to connect WebSocket:", err);
      // Continue without WebSocket - emotion data will still be saved to file
    }
  };

  // Start recording
  const startRecording = async () => {
    try {
      setError(null);
      emotionDataRef.current = [];

      // Request webcam and microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100,
          suppressLocalAudioPlayback: true,
        },
      });

      streamRef.current = stream;

      // Setup video element
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        videoRef.current.volume = 0;
        await videoRef.current.play();
      }

      // Setup canvas for frame extraction
      if (canvasRef.current && videoRef.current) {
        const canvas = canvasRef.current;
        const video = videoRef.current;
        const context = canvas.getContext("2d");

        // Set canvas size to match video
        canvas.width = video.videoWidth || 640;
        canvas.height = video.videoHeight || 480;

        // Start frame capture at 30 FPS
        frameIntervalRef.current = setInterval(() => {
          if (video && video.readyState === video.HAVE_ENOUGH_DATA) {
            context.drawImage(video, 0, 0, canvas.width, canvas.height);
            const imageData = context.getImageData(0, 0, canvas.width, canvas.height);

            // Dispatch custom event for MorphCast SDK
            if (typeof window.CY !== "undefined" && window.CY.modules) {
              try {
                window.dispatchEvent(
                  new CustomEvent(window.CY.modules().CAMERA.eventName, {
                    detail: imageData,
                  })
                );
              } catch (e) {
                // Fallback: SDK may process frames automatically
                console.warn("Could not dispatch camera event:", e);
              }
            }
          }
        }, 1000 / 30); // 30 FPS
      }

      // Start MorphCast SDK
      if (morphCastRef.current) {
        morphCastRef.current.start();
      }

      // Connect WebSocket
      connectWebSocket();

      // Setup MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/webm; codecs="vp8,opus"',
        audioBitsPerSecond: 128000,
        videoBitsPerSecond: 2500000,
      });

      const chunks = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const durationMs = startedAtRef.current ? Date.now() - startedAtRef.current : 0;
        const rawBlob = new Blob(chunks, { type: "video/webm" });
        const blob = await withFixedWebmDuration(rawBlob, durationMs);
        if (onRecordingStop) {
          onRecordingStop({
            videoBlob: blob,
            emotionData: emotionDataRef.current,
          });
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      startedAtRef.current = Date.now();
      mediaRecorder.start();
      setIsRecording(true);

      if (onRecordingStart) {
        onRecordingStart();
      }
    } catch (err) {
      console.error("Error starting webcam recording:", err);
      setError(err.message || "Failed to start webcam recording");
      setIsRecording(false);
    }
  };

  // Stop recording
  const stopRecording = () => {
    try {
      // Stop anti-fraud monitoring
      stopWebcamMonitoring();

      // Stop MediaRecorder
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }

      // Stop frame capture
      if (frameIntervalRef.current) {
        clearInterval(frameIntervalRef.current);
        frameIntervalRef.current = null;
      }

      // Stop MorphCast SDK
      if (morphCastRef.current) {
        morphCastRef.current.stop();
      }

      // Close WebSocket
      if (websocketRef.current) {
        websocketRef.current.close();
        websocketRef.current = null;
      }

      // Stop all tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      // Clear video element
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }

      setIsRecording(false);
    } catch (err) {
      console.error("Error stopping webcam recording:", err);
      setError(err.message || "Failed to stop webcam recording");
    }
  };

  // Expose start/stop methods via ref
  React.useImperativeHandle(ref, () => ({
    start: startRecording,
    stop: stopRecording,
    isRecording: () => isRecording,
    getWebcamChecksCount: () => webcamChecksCountRef.current,
    isFaceDetected: () => faceDetected,
    getStream: () => streamRef.current,
  }));

  return (
    <div className="webcam-recorder" style={{ display: "none" }}>
      <video ref={videoRef} autoPlay playsInline muted style={{ display: "none" }} />
      <canvas ref={canvasRef} style={{ display: "none" }} />
      {error && <div className="error">Error: {error}</div>}
    </div>
  );
});

export default WebcamRecorder;

